/**
 * The home's quick actions: six boards, three shown per visit. Each card's
 * prompt and the header the home places for it come from one board in
 * `homeBoards.ts`, so the two never disagree.
 */

import type { CanvasComposerSuggestion } from "@/pages/canvas/config/canvasComposerSuggestions";
import {
  Lightbulb,
  Newspaper,
  Orbit,
  Palette,
  Puzzle,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import type { HomeHeaderPlacement } from "./dispatch";
import {
  HOME_BOARDS,
  HOME_HEADER_HEIGHT,
  HOME_HEADER_WIDTH,
  homeBoard,
  quickActionPrompt,
  renderHomeHeader,
} from "./homeBoards";

const CARDS: Record<string, { label: string; hint: string; icon: LucideIcon }> =
  {
    brainstorm: {
      label: "Brainstorm ideas",
      hint: "Spin up a wall of possibilities",
      icon: Lightbulb,
    },
    explain: {
      label: "Explain something visually",
      hint: "Why we have seasons, as an explorable",
      icon: Orbit,
    },
    game: {
      label: "Make a little game",
      hint: "A word puzzle you can play right away",
      icon: Puzzle,
    },
    brand: {
      label: "Create a brand",
      hint: "A Lisbon coffee roaster, from logo to landing page",
      icon: Palette,
    },
    headlines: {
      label: "Summarize today's headlines",
      hint: "Today's news, in vintage newspaper print",
      icon: Newspaper,
    },
    art: {
      label: "Generate living art",
      hint: "A flow field that draws itself",
      icon: Sparkles,
    },
  };

export const HOME_QUICK_ACTIONS: readonly CanvasComposerSuggestion[] =
  HOME_BOARDS.map((board) => ({
    id: board.id,
    ...CARDS[board.id]!,
    prompt: quickActionPrompt(board),
  }));

/** The header a quick action's canvas starts with; nothing for a typed prompt. */
export function homeHeaderPlacement(
  id: string,
): HomeHeaderPlacement | undefined {
  const board = homeBoard(id);
  if (!board) return undefined;
  return {
    title: board.header.title,
    file: board.file,
    html: renderHomeHeader(board),
    width: HOME_HEADER_WIDTH,
    height: HOME_HEADER_HEIGHT,
  };
}

export const HOME_QUICK_ACTION_COUNT = 3;

/** Three of the six in a random order: a partial shuffle, drawn once per visit. */
export function pickQuickActions(
  random: () => number = Math.random,
): CanvasComposerSuggestion[] {
  const pool = [...HOME_QUICK_ACTIONS];
  for (let i = 0; i < HOME_QUICK_ACTION_COUNT; i++) {
    const j = i + Math.floor(random() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  return pool.slice(0, HOME_QUICK_ACTION_COUNT);
}
